/**
 * MapScene — connected node-graph map.
 *
 * Draws the map as a web of interconnected nodes. The player can
 * move freely between connected nodes. A player avatar sits at
 * the current position. Visited nodes are dimmed but revisitable.
 *
 * Supports maps larger than the viewport via camera panning:
 * click-drag the background, or the camera auto-follows the player.
 *
 * Node visual states:
 *   unvisited — full color, normal alpha
 *   visited   — desaturated, low alpha
 *   available — full color, pulsing, interactive
 */

declare const Phaser: any;

import { BaseScene } from "../../framework/BaseScene";
import { Depth } from "../../framework/Depth";
import { RunState } from "../data/RunState";
import { GameMap, MapNode, RoomType, generateMap } from "../data/MapData";
import { generateAllSprites } from "../sprites/SpriteFactory";

const ROOM_COLORS: Record<RoomType, number> = {
  battle: 0xef4444,
  elite: 0xa855f7,
  shop: 0xeab308,
  treasure: 0xfbbf24,
  rest: 0x22c55e,
  event: 0x6366f1,
  boss: 0x991b1b,
};

const ROOM_ICON_KEYS: Record<RoomType, string> = {
  battle: "icon_battle",
  elite: "icon_elite",
  shop: "icon_shop",
  treasure: "icon_treasure",
  rest: "icon_rest",
  event: "icon_event",
  boss: "icon_boss",
};

const ROOM_LABELS: Record<RoomType, string> = {
  battle: "Battle",
  elite: "Elite",
  shop: "Shop",
  treasure: "Treasure",
  rest: "Rest",
  event: "Event",
  boss: "Boss",
};

const SCENE_KEYS: Record<RoomType, string> = {
  battle: "BattleScene",
  elite: "BattleScene",
  shop: "ShopScene",
  treasure: "TreasureScene",
  rest: "RestScene",
  event: "EventScene",
  boss: "BattleScene",
};

/** Visual alpha values for each node state. */
const ALPHA_UNVISITED = 0.85;
const ALPHA_VISITED = 0.3;
const ALPHA_AVAILABLE = 1.0;

/** Per-node visual bundle. */
interface NodeVisuals {
  circle: any;
  icon: any;
  label: any;
  alertIcon: any | null;
}

export class MapScene extends BaseScene {
  private map!: GameMap;
  private run!: RunState;
  private nodeVisuals: Map<string, NodeVisuals> = new Map();
  private hudTexts: { hp?: any; gold?: any } = {};

  /** Avatar container — holds sprite + label, positioned at node centre. */
  private avatarContainer!: any;
  private playerAvatar!: any;

  private availableTweens: any[] = [];

  /** Camera drag state. */
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private camStartX = 0;
  private camStartY = 0;
  /** Whether a real drag occurred (moved more than threshold). */
  private didDrag = false;
  /** True while avatar is tweening to a node — blocks all input. */
  private isMoving = false;

  constructor() {
    super({ key: "MapScene" });
  }

  onReady(data?: any): void {
    this.run = this.game.registry.get("runState");
    this.map = generateMap(this.run.mapSeed);

    // Generate sprite textures once (idempotent)
    if (!this.textures.exists("player_map")) {
      generateAllSprites(this);
    }

    // Handle return from a pushed scene
    if (data && data._returnResult) {
      this.processResult(data._returnResult);
    }

    this.setupCamera();
    this.drawMap();
    this.drawPlayerAvatar();
    this.drawHUD();
    this.applyNodeStates();
    this.setupDragPan();

    // Centre camera on the player's current position
    this.panCameraToNode(this.run.currentNodeId ?? this.map.startNodeId, false);
  }

  onUpdate(_time: number, _delta: number): void {}

  // --- Camera ---

  private setupCamera(): void {
    const cam = this.cameras.main;
    cam.setBounds(0, 0, this.map.width, this.map.height);
  }

  private panCameraToNode(nodeId: string, animate: boolean = true): void {
    const node = this.map.nodes.get(nodeId);
    if (!node) return;

    const cam = this.cameras.main;
    if (animate) {
      cam.pan(node.x, node.y, 400, "Power2");
    } else {
      cam.centerOn(node.x, node.y);
    }
  }

  private setupDragPan(): void {
    this.input.on("pointerdown", (p: any) => {
      this.isDragging = true;
      this.didDrag = false;
      this.dragStartX = p.x;
      this.dragStartY = p.y;
      const cam = this.cameras.main;
      this.camStartX = cam.scrollX;
      this.camStartY = cam.scrollY;
    });

    this.input.on("pointermove", (p: any) => {
      if (!this.isDragging) return;
      const dx = this.dragStartX - p.x;
      const dy = this.dragStartY - p.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        this.didDrag = true;
        const cam = this.cameras.main;
        cam.scrollX = this.camStartX + dx;
        cam.scrollY = this.camStartY + dy;
      }
    });

    this.input.on("pointerup", () => {
      this.isDragging = false;
    });
  }

  // --- Drawing ---

  private drawMap(): void {
    // Clear stale references from previous scene run
    this.nodeVisuals.clear();
    this.availableTweens = [];

    // Background
    this.add.rectangle(
      this.map.width / 2, this.map.height / 2,
      this.map.width, this.map.height,
      0x0f1b2d,
    );

    // Title
    this.add.text(this.map.width / 2, 14, "~ Word RPG ~", {
      fontFamily: "system-ui, sans-serif",
      fontSize: "16px",
      color: "#6b7280",
    }).setOrigin(0.5, 0);

    // Draw connections (lines behind nodes)
    const drawnEdges = new Set<string>();
    for (const node of this.map.nodes.values()) {
      for (const connId of node.connections) {
        const edgeKey = [node.id, connId].sort().join("-");
        if (drawnEdges.has(edgeKey)) continue;
        drawnEdges.add(edgeKey);

        const target = this.map.nodes.get(connId);
        if (!target) continue;

        const line = this.add.graphics();
        line.lineStyle(2, 0x374151, 0.4);
        line.beginPath();
        line.moveTo(node.x, node.y);
        line.lineTo(target.x, target.y);
        line.strokePath();
      }
    }

    // Draw nodes — all start at unvisited alpha; applyNodeStates fixes them up
    for (const node of this.map.nodes.values()) {
      this.createNodeVisuals(node);
    }
  }

  /** Create the visual objects for a node (circle + icon + label). */
  private createNodeVisuals(node: MapNode): void {
    const color = ROOM_COLORS[node.type];
    const iconKey = ROOM_ICON_KEYS[node.type];
    const radius = node.type === "boss" ? 22 : 16;

    // Circle
    const circle = this.add.circle(node.x, node.y, radius, color, 0.7);
    circle.setStrokeStyle(2, color);
    circle.setDepth(Depth.GAME);
    circle.setAlpha(ALPHA_UNVISITED);

    // Icon
    const iconScale = node.type === "boss" ? 2.2 : 1.6;
    const icon = this.add.image(node.x, node.y, iconKey);
    icon.setScale(iconScale);
    icon.setDepth(Depth.GAME + 1);
    icon.setAlpha(ALPHA_UNVISITED);

    // Label
    const label = this.add.text(node.x, node.y + radius + 5, ROOM_LABELS[node.type], {
      fontFamily: "system-ui, sans-serif",
      fontSize: "9px",
      color: "#6b7280",
    }).setOrigin(0.5, 0).setDepth(Depth.GAME);

    this.nodeVisuals.set(node.id, { circle, icon, label, alertIcon: null });
  }

  /** Set a node to a specific visual alpha. */
  private setNodeAlpha(nodeId: string, alpha: number): void {
    const v = this.nodeVisuals.get(nodeId);
    if (!v) return;
    v.circle.setAlpha(alpha);
    v.icon.setAlpha(alpha);
    v.label.setColor(alpha < 0.5 ? "#3b3b3b" : "#6b7280");
  }

  /**
   * Apply correct visual state to every node.
   * Called on init and after each move.
   */
  private applyNodeStates(): void {
    // 1. Stop all pulse tweens and reset scales
    for (const t of this.availableTweens) t.stop();
    this.availableTweens = [];

    // 2. Remove all interactivity
    for (const v of this.nodeVisuals.values()) {
      v.circle.removeInteractive();
      v.circle.removeAllListeners();
      v.circle.setScale(1);
    }

    // 3. Set base alpha for every node
    for (const [id, _v] of this.nodeVisuals) {
      if (this.run.visitedNodes.has(id)) {
        this.setNodeAlpha(id, ALPHA_VISITED);
      } else {
        this.setNodeAlpha(id, ALPHA_UNVISITED);
      }
    }

    // 4. Make connected nodes interactive (all are clickable for movement)
    //    but only pulse + brighten unvisited ones
    const available = this.getAvailableNodes();
    for (const nodeId of available) {
      const v = this.nodeVisuals.get(nodeId);
      const node = this.map.nodes.get(nodeId);
      if (!v || !node) continue;

      const visited = this.run.visitedNodes.has(nodeId);

      // Visited nodes stay dim — only unvisited get highlighted
      if (!visited) {
        this.setNodeAlpha(nodeId, ALPHA_AVAILABLE);

        // Pulse animation only for unvisited
        const tween = this.tweens.add({
          targets: v.circle,
          scaleX: 1.12,
          scaleY: 1.12,
          duration: 500,
          yoyo: true,
          repeat: -1,
          ease: "Sine.easeInOut",
        });
        this.availableTweens.push(tween);
      }

      // All connected nodes are clickable (for traversal)
      v.circle.setInteractive({ useHandCursor: true });
      v.circle.on("pointerup", () => {
        if (!this.didDrag && !this.isMoving) {
          this.selectNode(node);
        }
      });
    }

    // 5. Show/hide alert exclamation marks
    for (const [id, v] of this.nodeVisuals) {
      const isAlert = this.run.alertNodes.has(id) && !this.run.visitedNodes.has(id);
      if (isAlert && !v.alertIcon) {
        const node = this.map.nodes.get(id)!;
        const radius = node.type === "boss" ? 22 : 16;
        v.alertIcon = this.add.text(node.x + radius + 2, node.y - radius - 2, "!", {
          fontFamily: "system-ui, sans-serif",
          fontSize: "14px",
          color: "#fbbf24",
          fontStyle: "bold",
        }).setOrigin(0.5).setDepth(Depth.EFFECTS);
        // Pulse the exclamation
        const tween = this.tweens.add({
          targets: v.alertIcon,
          alpha: 0.4,
          duration: 600,
          yoyo: true,
          repeat: -1,
          ease: "Sine.easeInOut",
        });
        this.availableTweens.push(tween);
      } else if (!isAlert && v.alertIcon) {
        v.alertIcon.destroy();
        v.alertIcon = null;
      }
    }
  }

  private drawPlayerAvatar(): void {
    const nodeId = this.run.currentNodeId ?? this.map.startNodeId;
    const node = this.map.nodes.get(nodeId);
    if (!node) return;

    // Container positioned at node centre — avatar offset is relative to container
    const container = this.add.container(node.x, node.y);
    container.setDepth(Depth.EFFECTS);

    // Pixel art player sprite (offset up from node centre)
    const avatar = this.add.image(0, -26, "player_map");
    avatar.setScale(2.5);

    // "You" label
    const label = this.add.text(0, -44, "You", {
      fontFamily: "system-ui, sans-serif",
      fontSize: "9px",
      color: "#93c5fd",
      fontStyle: "bold",
    }).setOrigin(0.5);

    container.add([avatar, label]);

    // Gentle bob — animate the sprite within the container, not the container itself
    this.tweens.add({
      targets: avatar,
      y: -29,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });

    this.avatarContainer = container;
    this.playerAvatar = avatar;
  }

  private drawHUD(): void {
    const style = {
      fontFamily: "system-ui, sans-serif",
      fontSize: "13px",
      color: "#d1d5db",
    };

    const barY = 430;
    this.hudTexts.hp = this.ui.text(16, barY, `HP: ${this.run.player.hp}/${this.run.player.maxHp}`, style, "hud");
    this.hudTexts.gold = this.ui.text(200, barY, `Gold: ${this.run.player.gold}`, style, "hud");
  }

  // --- Interaction ---

  private getAvailableNodes(): string[] {
    if (this.run.currentNodeId === null) {
      const start = this.map.nodes.get(this.map.startNodeId);
      if (!start) return [];
      return [start.id];
    }

    const current = this.map.nodes.get(this.run.currentNodeId);
    if (!current) return [];

    return current.connections;
  }

  private selectNode(node: MapNode): void {
    if (this.isMoving) return;
    this.isMoving = true;

    const duration = 300;

    // Disable everything while moving
    for (const t of this.availableTweens) t.stop();
    this.availableTweens = [];
    for (const v of this.nodeVisuals.values()) {
      if (!v.circle.scene) continue;
      v.circle.removeInteractive();
      v.circle.removeAllListeners();
    }

    // Pan camera to destination
    this.panCameraToNode(node.id, true);

    // Move avatar container to the node's position
    this.tweens.add({
      targets: this.avatarContainer,
      x: node.x,
      y: node.y,
      duration,
      ease: "Power2",
      onComplete: () => {
        this.run.currentNodeId = node.id;
        this.isMoving = false;

        // If already visited, just passing through
        if (this.run.visitedNodes.has(node.id)) {
          this.applyNodeStates();
          return;
        }

        // Mark visited and enter the room
        this.run.visitedNodes.add(node.id);

        const sceneKey = SCENE_KEYS[node.type];
        const sceneData: any = { roomType: node.type };

        if (node.type === "elite") {
          sceneData.isElite = true;
        } else if (node.type === "boss") {
          sceneData.isBoss = true;
        }

        // Compute depth (0 = start, 1 = boss area) from Y position
        // Bottom of map = start (depth 0), top = boss (depth 1)
        sceneData.depth = 1 - (node.y / this.map.height);

        this.sm.push(sceneKey, sceneData);
      },
    });
  }

  // --- Return from room ---

  private processResult(result: any): void {
    if (!result) return;

    if (result.gold && result.outcome !== "victory") {
      this.run.player.addGold(result.gold);
    }
    if (result.hpChange) {
      this.run.player.heal(result.hpChange);
    }
    if (result.outcome === "defeat") {
      const newRun = (this.game.registry.get("createRunState") as Function)();
      this.game.registry.set("runState", newRun);
      this.run = newRun;
    }
  }
}
